import {
  type CountryCode,
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  isPossiblePhoneNumber,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';
import examples from 'libphonenumber-js/examples.mobile.json';

/**
 * Worldwide mobile number support (GIFTWEB-17), all driven by libphonenumber-js
 * so the country list, dial codes, placeholders and validation stay in step.
 */

export const DEFAULT_PHONE_COUNTRY: CountryCode = 'GB';

export type PhoneCountry = {
  code: CountryCode;
  name: string;
  dialCode: string; // e.g. "+44"
};

const regionNames = (() => {
  try {
    return new Intl.DisplayNames(['en-GB'], { type: 'region' });
  } catch {
    return null;
  }
})();

export const getCountryName = (code: CountryCode): string => {
  try {
    return regionNames?.of(code) || code;
  } catch {
    return code;
  }
};

/** Every country libphonenumber knows, alphabetical by English name. */
export const PHONE_COUNTRIES: readonly PhoneCountry[] = getCountries()
  .map((code) => ({
    code,
    name: getCountryName(code),
    dialCode: `+${getCountryCallingCode(code)}`,
  }))
  .sort((a, b) => a.name.localeCompare(b.name, 'en-GB'));

export const isPhoneCountry = (value: string): value is CountryCode =>
  PHONE_COUNTRIES.some((c) => c.code === value);

export const getDialCode = (country: CountryCode): string =>
  `+${getCountryCallingCode(country)}`;

/**
 * Example mobile number for the country, formatted the way the user is
 * expected to type it next to the dial-code chip: "7400 123456" for GB,
 * "20 123 4567" for HU, "201 555 0123" for US.
 */
export const getPhonePlaceholder = (country: CountryCode): string => {
  const example = getExampleNumber(country, examples);
  if (!example) return 'Mobile number';
  return example
    .formatInternational()
    .replace(`+${example.countryCallingCode}`, '')
    .trim();
};

type ValidateOptions = { allowTestNumbers?: boolean };

/**
 * Validity of a number typed for the selected country. A number typed with
 * its own "+CC" prefix is judged against that prefix instead, as libphonenumber
 * does. `allowTestNumbers` relaxes validity to "possible" (dev / UAT numbers).
 */
export const isPhoneValidForCountry = (
  raw: string,
  country: CountryCode,
  { allowTestNumbers = false }: ValidateOptions = {},
): boolean => {
  const value = raw.trim();
  if (!value) return true;
  return allowTestNumbers
    ? isPossiblePhoneNumber(value, country)
    : isValidPhoneNumber(value, country);
};

/** E.164 ("+447516405902") for a number typed for the selected country, or null. */
export const toE164 = (raw: string, country: CountryCode): string | null => {
  const value = raw.trim();
  if (!value) return null;
  return parsePhoneNumberFromString(value, country)?.number ?? null;
};

/**
 * Inverse of `toE164` for pre-filling the field from the store: the country
 * the number belongs to and the national part formatted for display next to
 * the dial-code chip ("7516 405902"). Returns null for anything that is not
 * a parseable international number.
 */
export const splitE164 = (
  e164: string | undefined | null,
): { country: CountryCode; national: string } | null => {
  if (!e164 || !e164.startsWith('+')) return null;
  const parsed = parsePhoneNumberFromString(e164);
  if (!parsed?.country) return null;
  return {
    country: parsed.country,
    national: parsed
      .formatInternational()
      .replace(`+${parsed.countryCallingCode}`, '')
      .trim(),
  };
};

/** Country implied by a number typed with its own "+CC" prefix, if any. */
export const detectCountryFromInput = (raw: string): CountryCode | null => {
  const value = raw.trim();
  if (!value.startsWith('+')) return null;
  return parsePhoneNumberFromString(value)?.country ?? null;
};
